import { useCallback, useState } from 'react';
import { endOfDay, parseISO, startOfDay, startOfWeek, subDays } from 'date-fns';
import { maxBy } from 'lodash/fp';
import { ORG_IDS } from '@/constants';
import { useCurrentOrganization } from '@/core/hooks/useCurrentOrganization';
import { useCurrentUser } from '@/core/hooks/useCurrentUser';
import type { FiltersFlags, FiltersState } from '@/core/modules/MapPanelSources';
import { Formatter } from '@/core/utils/Formatter';
import {
  type IncidentItem,
  IncidentsDateTimeRange,
} from '@/server/services/GoogleBigQuery/BigQueryIncidentsClient.types';

export const DEFAULT_FILTERS_STATE: FiltersState = {
  incidentTypeID: [],
  severityLevel: [],
  incidentLocations: [],
  verificationSources: [],
  industries: [],
  dateRange: [
    {
      from: Formatter.toFilterDateTime(startOfWeek(new Date())),
      to: Formatter.toFilterDateTime(new Date()),
    },
  ], // Defaults for current week
};

const getDefaultDaysForOrg = (orgId?: string) => {
  if (
    orgId &&
    [
      ORG_IDS.CERBERUS,
      ORG_IDS.MASS_ATROCITY_MONITOR,
      ORG_IDS.NLI_UAE_Climate_Simulation,
      ORG_IDS.UTD_18_JUNE,
      ORG_IDS.CENTRAL_ASIA_CENTER,
      ORG_IDS.ZF_LIFETEC_DEMO,
      ORG_IDS.ZF_LIFETEC,
      ORG_IDS.DM_AUTO,
      ORG_IDS.NLI,
    ].includes(orgId)
  ) {
    return 365;
  }

  if (orgId === ORG_IDS.DAMAGE_ASSESSMENT) {
    return 90;
  }

  return 30;
};

export const useFiltersState = () => {
  const [initialized, setInitialized] = useState(false);
  const { organizationModel } = useCurrentOrganization();
  const [filterFlags, setFilterFlags] = useState<FiltersFlags>(
    Object.fromEntries(Object.keys(DEFAULT_FILTERS_STATE).map((key) => [key, true])) as FiltersFlags,
  ); // all filters available by default

  const [defaultFilters, setDefaultFilters] = useState<FiltersState>({
    ...DEFAULT_FILTERS_STATE,
    ...(organizationModel?.hasIncidentQueryLimit() ? { dateRange: [] } : {}), // no default date range for org with incidents limit
  });
  const [filters, setFilters] = useState<FiltersState>({ ...defaultFilters });
  const currentUser = useCurrentUser();

  const updateDefaultDateRangeFilters = useCallback(
    ({
      incidentsData,
      incidentsDateTimeRange,
    }: {
      incidentsData?: IncidentItem[];
      incidentsDateTimeRange?: IncidentsDateTimeRange;
    }) => {
      if (initialized) {
        return;
      }

      // Prefer incidentDateTimeRange if available, otherwise fall back to incidentsData
      let latestDateTime: string | null = null;

      if (incidentsDateTimeRange?.max) {
        latestDateTime = incidentsDateTimeRange.max;
      } else if (incidentsData && incidentsData.length > 0) {
        const latestItem = maxBy((item: IncidentItem) => parseISO(item.IncidentDateTime))(incidentsData);
        if (latestItem) {
          latestDateTime = latestItem.IncidentDateTime;
        }
      }

      if (latestDateTime) {
        setInitialized(true);
        const fromDate = startOfDay(
          // TODO: Demo purpose
          // 365 days back for cerberus, 90 for damage assessment org, 30 by default
          subDays(parseISO(latestDateTime), getDefaultDaysForOrg(currentUser.orgId)),
        );

        const toDate = endOfDay(parseISO(latestDateTime));

        const dateRange: FiltersState['dateRange'] = [
          {
            from: Formatter.toFilterDateTime(fromDate),
            to: Formatter.toFilterDateTime(toDate), // yyyy-MM-dd => yyyy-MM-ddT00:00:00.000Z
          },
        ];

        setDefaultFilters((prev) => ({
          ...prev,
          dateRange,
        }));

        // Set filters to default values
        setFilters((prev) => ({
          ...prev,
          dateRange,
        }));
      }
    },
    [initialized, currentUser.orgId],
  );

  return {
    filters,
    setFilters,
    defaultFilters,
    updateDefaultDateRangeFilters,
    filterFlags,
    setFilterFlags,
  };
};
